/**
 * Utilidades para el motor de formularios dinámicos por `slug`
 * (`dynamicFormService.js`). A diferencia de `utils/surveyForm.js`
 * (preguntas referenciadas por `order`, agrupadas en `block`), acá las
 * condiciones referencian la `key` de otra pregunta y no hay pasos:
 * el formulario se muestra completo en una sola página.
 */

/**
 * Compara el valor actual de una respuesta contra el valor esperado
 * de una condición, tolerando diferencias de tipo entre lo que guarda
 * el formulario (ej. `true` booleano para preguntas `boolean`) y lo
 * que puede llegar desde la API (ej. `"true"`, `"1"`, `1`). Sin esto,
 * condiciones como "mostrar teléfono/correo si autorizó == true" no
 * se cumplían cuando la API mandaba el valor esperado como string.
 */
function valuesMatch(currentValue, expectedValue) {
  if (currentValue === expectedValue) return true;

  const isBooleanish = (v) => typeof v === 'boolean' || v === 'true' || v === 'false' || v === 1 || v === 0 || v === '1' || v === '0';
  if (isBooleanish(currentValue) && isBooleanish(expectedValue)) {
    const toBool = (v) => v === true || v === 'true' || v === 1 || v === '1';
    return toBool(currentValue) === toBool(expectedValue);
  }

  // Compara como texto por si uno es string y el otro número (ej. ids de catálogo).
  return String(currentValue) === String(expectedValue);
}

/** Evalúa una condición individual contra las respuestas actuales. */
function evaluateCondition(condition, answers, questionsByKey) {
  const targetQuestion = questionsByKey[condition.questionKey];
  if (!targetQuestion) return true;
  const currentValue = answers[targetQuestion.id];

  switch (condition.operator) {
    case 'not_equals':
      return !valuesMatch(currentValue, condition.value);
    case 'equals':
    default:
      return valuesMatch(currentValue, condition.value);
  }
}

/** Mapa `key -> pregunta`, para resolver condiciones rápidamente. */
export function buildQuestionsByKey(questions) {
  return questions.reduce((acc, question) => {
    acc[question.key] = question;
    return acc;
  }, {});
}

/** `true` si todas las condiciones de la pregunta se cumplen (o no tiene). */
export function isQuestionVisible(question, answers, questionsByKey) {
  if (!question.conditions || question.conditions.length === 0) return true;
  return question.conditions.every((condition) => evaluateCondition(condition, answers, questionsByKey));
}

function isAnswerEmpty(value) {
  return value === undefined || value === null || value === '';
}

/**
 * Valida las preguntas visibles: obligatorias sin responder, y
 * longitud máxima (`validation.max`) para `textarea`.
 *
 * @returns {Object} mapa `questionId -> mensaje de error`
 */
export function validateVisibleQuestions(questions, answers) {
  const errors = {};
  for (const question of questions) {
    const value = answers[question.id];

    if (question.required && isAnswerEmpty(value)) {
      errors[question.id] = 'Este campo es obligatorio.';
      continue;
    }

    if ((question.type === 'textarea' || question.type === 'text') && typeof value === 'string' && question.validation?.max) {
      if (value.length > question.validation.max) {
        errors[question.id] = `Máximo ${question.validation.max} caracteres.`;
      }
    }

    if (question.type === 'date' && value && question.validation?.afterOrEqual === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(`${value}T00:00:00`);
      if (selected < today) {
        errors[question.id] = 'Selecciona una fecha igual o posterior a hoy.';
      }
    }

    if (question.type === 'phone' && value) {
      const digits = String(value).replace(/\D/g, '');
      const { minDigits, maxDigits } = question.validation || {};
      if (minDigits && digits.length < minDigits) {
        errors[question.id] = `El teléfono debe tener al menos ${minDigits} dígitos.`;
      } else if (maxDigits && digits.length > maxDigits) {
        errors[question.id] = `El teléfono debe tener máximo ${maxDigits} dígitos.`;
      }
    }

    if (question.type === 'email' && value) {
      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (!isValidEmail) {
        errors[question.id] = 'Ingresa un correo electrónico válido.';
      } else if (question.validation?.max && value.length > question.validation.max) {
        errors[question.id] = `Máximo ${question.validation.max} caracteres.`;
      }
    }
  }
  return errors;
}
